import React, { useState } from "react";
import {
  Calendar,
  BarChart3,
  Clock,
  TrendingUp,
  Eye,
} from "lucide-react";
import { ClientLogo } from "../../components/ClientLogo";
import type { CampaignReportResponse } from "../../types";
import { MetricCard } from "../../components/MetricCard";
import { BarChart } from "../../components/BarChart";
import { CARD_BG, PAGE_BG, SHADOW } from "../../config/theme";
import { SectionToggleWrapper } from "../../components/SectionToggleWrapper";
import { AiInsightsSection } from "../../components/AiInsightsSection";
import { buildOnsiteAwarenessLayoutViewModel } from "./onsiteAwarenessLayoutViewModel";

interface OnsiteAwarenessLayoutProps {
  data: CampaignReportResponse;
  reportId?: string;
  onNewUpload?: () => void;
  clientId?: string;
}

// Awareness-specific thresholds
const CTR_THRESHOLDS = {
  EXCELLENT: 0.15, // 0.15%
  GOOD: 0.08, // 0.08%
  AVERAGE: 0.05, // 0.05%
} as const;

const PDP_VIEW_RATE_THRESHOLDS = {
  EXCELLENT: 0.1, // 0.1% (10 per 10,000 impressions)
  GOOD: 0.05, // 0.05%
  AVERAGE: 0.02, // 0.02%
} as const;

const ADD_TO_CART_RATE_THRESHOLDS = {
  EXCELLENT: 0.08, // 0.08%
  GOOD: 0.04, // 0.04%
  AVERAGE: 0.02, // 0.02%
} as const;

const ECPM_THRESHOLDS = {
  EFFICIENT: 7, // Below $7 eCPM is efficient
  AVERAGE: 10, // $7-10 is average
  EXPENSIVE: 10, // Above $10 is expensive
} as const;

// ==================== BACKGROUND COLOR CONFIGURATION ====================
// Theme configuration imported from central config
// To change card background colors, edit: frontend/src/config/theme.ts
// Options: 'slate' | 'stone' | 'neutral' | 'gray'
const BACKGROUND_CLASSES = PAGE_BG;
const CARD_BACKGROUND_CLASSES = CARD_BG;
// ========================================================================

/**
 * Specialized layout for Onsite Display Ads focused on awareness metrics.
 *
 * Highlights:
 * - Impressions & Reach metrics
 * - Engagement funnel (Impressions → Clicks → PDP Views → Add to Cart → Conversions)
 * - eCPM efficiency analysis
 * - Engagement rates over time
 */
export const OnsiteAwarenessLayout: React.FC<OnsiteAwarenessLayoutProps> = ({
  data,
  reportId,
  clientId,
}) => {
  const { summary } = data;
  const resolvedClientId = clientId ?? "ajinomoto";
  const viewModel = buildOnsiteAwarenessLayoutViewModel(data);
  const {
    avgECPM,
    avgPDPViewRate,
    avgAddToCartRate,
    avgCTR,
    impressions,
    clicks,
  } = viewModel.awarenessSummary;
  const {
    sortedCampaigns,
    impressionLabels: campaignImpressionLabels,
    impressionValues: campaignImpressionValues,
    ctrLabels: campaignCTRLabels,
    ctrValues: campaignCTRValues,
  } = viewModel.campaignCharts;
  const {
    labels: dailyLabels,
    impressionsDataset: dailyImpressionsDataset,
    engagementDatasets: dailyEngagementDatasets,
  } = viewModel.dailyCharts;

  const [hiddenSections, setHiddenSections] = useState<Record<string, boolean>>(
    {},
  );

  const hideSection = (sectionId: string) =>
    setHiddenSections((prev) => ({ ...prev, [sectionId]: true }));

  const restoreSection = (sectionId: string) =>
    setHiddenSections((prev) => ({ ...prev, [sectionId]: false }));

  const isSectionHidden = (sectionId: string) =>
    Boolean(hiddenSections[sectionId]);

  return (
    <div className={`py-16 px-8 ${BACKGROUND_CLASSES}`} data-dashboard>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Campaign Report: Walmart Onsite Display: GYOZA
              </h1>
              <div className="flex items-center gap-4 mt-3 text-sm text-gray-600 dark:text-[#B5A9AA]">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200">
                  Onsite Display Ads
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {summary.date_range_start} to {summary.date_range_end}
                </span>
                <span className="flex items-center gap-1">
                  <BarChart3 className="w-3 h-3" />
                  {data.records_processed} records
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {summary.total_days} days
                </span>
              </div>
            </div>
            <div>
              <ClientLogo
                clientId={resolvedClientId}
                className="h-16 object-contain"
                fallback={
                  <div className="h-16 w-16 bg-gray-200 dark:bg-[#5B5353] rounded" />
                }
              />
            </div>
          </div>
        </div>

        <AiInsightsSection data={data} reportId={reportId} />

        {/* Key Awareness Metrics */}
        <SectionToggleWrapper
          sectionId="onsite-overview"
          title="Awareness & Reach Performance"
          hidden={isSectionHidden("onsite-overview")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Awareness & Reach Performance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <MetricCard
              title="Total Impressions"
              value={
                impressions >= 1000000
                  ? `${(impressions / 1000000).toFixed(1)}M`
                  : impressions >= 1000
                    ? `${(impressions / 1000).toFixed(0)}k`
                    : impressions.toLocaleString()
              }
              subtitle={`${Math.round(impressions / summary.total_days).toLocaleString()} per day`}
              trend="neutral"
            />
            <MetricCard
              title="eCPM"
              value={`$${avgECPM.toFixed(2)}`}
              subtitle={
                avgECPM <= ECPM_THRESHOLDS.EFFICIENT
                  ? "Efficient cost"
                  : avgECPM <= ECPM_THRESHOLDS.AVERAGE
                    ? "Average cost"
                    : "Above average cost"
              }
              trend={
                avgECPM <= ECPM_THRESHOLDS.EFFICIENT
                  ? "up"
                  : avgECPM <= ECPM_THRESHOLDS.AVERAGE
                    ? "neutral"
                    : "down"
              }
            />
            <MetricCard
              title="Click Rate"
              value={`${avgCTR.toFixed(3)}%`}
              subtitle={
                avgCTR >= CTR_THRESHOLDS.EXCELLENT
                  ? "Excellent engagement"
                  : avgCTR >= CTR_THRESHOLDS.GOOD
                    ? "Good engagement"
                    : avgCTR >= CTR_THRESHOLDS.AVERAGE
                      ? "Average engagement"
                      : "Below average"
              }
              trend={
                avgCTR >= CTR_THRESHOLDS.GOOD
                  ? "up"
                  : avgCTR >= CTR_THRESHOLDS.AVERAGE
                    ? "neutral"
                    : "down"
              }
            />
            <MetricCard
              title="Total Clicks"
              value={clicks.toLocaleString()}
              subtitle={`${Math.round(clicks / summary.total_days).toLocaleString()} per day`}
              trend="neutral"
            />
            <MetricCard
              title="PDP View Rate"
              value={`${avgPDPViewRate.toFixed(2)}%`}
              subtitle={
                avgPDPViewRate >= PDP_VIEW_RATE_THRESHOLDS.EXCELLENT
                  ? "Excellent interest"
                  : avgPDPViewRate >= PDP_VIEW_RATE_THRESHOLDS.GOOD
                    ? "Good interest"
                    : "Building interest"
              }
              trend={
                avgPDPViewRate >= PDP_VIEW_RATE_THRESHOLDS.GOOD
                  ? "up"
                  : avgPDPViewRate >= PDP_VIEW_RATE_THRESHOLDS.AVERAGE
                    ? "neutral"
                    : "down"
              }
            />
            <MetricCard
              title="Add to Cart Rate"
              value={`${avgAddToCartRate.toFixed(2)}%`}
              subtitle={
                avgAddToCartRate >= ADD_TO_CART_RATE_THRESHOLDS.EXCELLENT
                  ? "Excellent intent"
                  : avgAddToCartRate >= ADD_TO_CART_RATE_THRESHOLDS.GOOD
                    ? "Good intent"
                    : "Building intent"
              }
              trend={
                avgAddToCartRate >= ADD_TO_CART_RATE_THRESHOLDS.GOOD
                  ? "up"
                  : avgAddToCartRate >= ADD_TO_CART_RATE_THRESHOLDS.AVERAGE
                    ? "neutral"
                    : "down"
              }
            />
          </div>
        </SectionToggleWrapper>

        {/* Brand Awareness Performance */}
        <SectionToggleWrapper
          sectionId="onsite-campaign-awareness"
          title="Campaign Awareness Distribution"
          hidden={isSectionHidden("onsite-campaign-awareness")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Campaign Awareness Distribution
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* CTR by Ad Group */}
            <div
              className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6 h-[400px] flex flex-col`}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Engagement Rate by Ad Group (CTR)
              </h3>
              <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
                Higher CTR indicates stronger engagement
              </p>
              <div className="flex-1 min-h-0">
                <BarChart
                  labels={campaignCTRLabels}
                  datasets={[
                    {
                      label: "CTR (%)",
                      data: campaignCTRValues,
                      backgroundColor: "rgba(34, 197, 94, 0.8)", // green-500
                    },
                  ]}
                  horizontal={true}
                  showLegend={false}
                  showDataLabels={true}
                  height="h-full"
                />
              </div>
            </div>
            {/* Impression Distribution by Ad Group */}
            <div
              className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6 h-[400px] flex flex-col`}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Impressions by Ad Group
              </h3>
              <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
                Total: {impressions.toLocaleString()} impressions
              </p>
              <div className="flex-1 min-h-0">
                <BarChart
                  labels={campaignImpressionLabels}
                  datasets={[
                    {
                      label: "Impressions",
                      data: campaignImpressionValues,
                      backgroundColor: "rgba(139, 92, 246, 0.8)", // violet-500
                    },
                  ]}
                  horizontal={true}
                  showLegend={false}
                  showDataLabels={true}
                  height="h-full"
                />
              </div>
            </div>
          </div>
        </SectionToggleWrapper>

        {/* Daily Trends */}
        <SectionToggleWrapper
          sectionId="onsite-daily-trends"
          title="Daily Awareness Trends"
          hidden={isSectionHidden("onsite-daily-trends")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Daily Awareness Trends
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily CTR */}
            <div
              className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Total Daily Engagement Rate (CTR)
              </h3>
              <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
                Engagement trends over {summary.total_days} days
              </p>
              <BarChart
                labels={dailyLabels}
                datasets={dailyEngagementDatasets}
                horizontal={false}
                showLegend={false}
                showDataLabels={false}
                height="h-64"
              />
            </div>

            {/* Daily Impressions */}
            <div
              className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Total Daily Impressions
              </h3>
              <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
                Reach trends over {summary.total_days} days
              </p>
              <BarChart
                labels={dailyLabels}
                datasets={dailyImpressionsDataset}
                horizontal={false}
                showLegend={false}
                showDataLabels={false}
                height="h-64"
              />
            </div>
          </div>
        </SectionToggleWrapper>

        {/* Campaign Performance Table */}
        <SectionToggleWrapper
          sectionId="onsite-campaign-details"
          title="Campaign Awareness Details"
          hidden={isSectionHidden("onsite-campaign-details")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <div
            className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Campaign Awareness Details
            </h3>
            <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
              {summary.campaign_count} campaigns sorted by impressions
            </p>
            <div className="overflow-x-auto" data-export-disable-scroll>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-[#403838]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Impressions
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      eCPM
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      CTR
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Clicks
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Spend
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Days
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`${CARD_BACKGROUND_CLASSES} divide-y divide-gray-200 dark:divide-gray-700`}
                >
                  {sortedCampaigns.map((campaign) => {
                    const ctr =
                      campaign.total_impressions > 0
                        ? (campaign.total_clicks /
                            campaign.total_impressions) *
                          100
                        : 0;
                    const ecpm =
                      campaign.total_impressions > 0
                        ? (parseFloat(campaign.total_spend) /
                            campaign.total_impressions) *
                          1000
                        : 0;

                    return (
                      <tr
                        key={campaign.campaign_name}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <td
                          className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white max-w-xs truncate"
                          title={campaign.campaign_name}
                        >
                          {campaign.campaign_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-white">
                          {campaign.total_impressions >= 1000000
                            ? `${(campaign.total_impressions / 1000000).toFixed(1)}M`
                            : campaign.total_impressions >= 1000
                              ? `${(campaign.total_impressions / 1000).toFixed(0)}k`
                              : campaign.total_impressions.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-[#B5A9AA]">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              ecpm <= ECPM_THRESHOLDS.EFFICIENT
                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                : ecpm <= ECPM_THRESHOLDS.AVERAGE
                                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                                  : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                            }`}
                          >
                            ${ecpm.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-[#B5A9AA]">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              ctr >= CTR_THRESHOLDS.GOOD
                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                : ctr >= CTR_THRESHOLDS.AVERAGE
                                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
                            }`}
                          >
                            {ctr.toFixed(3)}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-[#B5A9AA]">
                          {campaign.total_clicks.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-[#B5A9AA]">
                          $
                          {parseFloat(campaign.total_spend).toLocaleString(
                            "en-US",
                            { minimumFractionDigits: 2 },
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-[#B5A9AA]">
                          {campaign.days_active}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </SectionToggleWrapper>
      </div>
    </div>
  );
};
